// deployment_class: customer_env_allowed
/**
 * The wire between the live executive payload and the content seam.
 *
 * `GET /v3/executive/dashboard` has existed and worked since it shipped, and
 * `DashboardContentProvider` has existed with zero consumers. This hook is the
 * connection that was missing — the reason the cockpit has been reading
 * hand-authored constants (UI-B2 · UI-B3 · UI-B7) while a real read model sat
 * one fetch away.
 *
 * ── Why `null` for loading AND error, deliberately ─────────────────────────
 * The seam has three states, and the distinction that matters is `null`
 * (NO SUPPLIER — nothing is wired here) versus `{ empty: true }` (a supplier
 * answered and had nothing). While a request is in flight, or after it failed,
 * we genuinely have no content — so `null` is the truthful value. Returning
 * `{ empty: true }` there would assert that the agency HAS no figures, which is
 * a claim we have not earned and which reads identically to a real empty
 * agency.
 *
 * `isError` is returned alongside rather than folded in, so a caller can say
 * "could not load" instead of silently rendering an unwired-looking cockpit. The
 * seam itself has no error state; giving it one is a change to the contract and
 * belongs with the design that needs it, not smuggled in here.
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../shell/api/api-client-base';
import { toDashboardContent, type WireExecutiveDashboard } from './executive-content';
import type { DashboardContent } from './dashboard-source';

/** The one route this reads. Module-private: nothing outside needs the literal. */
const EXECUTIVE_DASHBOARD_PATH = '/v3/executive/dashboard';

export interface ExecutiveContentResult {
  /** Content for the seam, or `null` when nothing has supplied any yet. */
  readonly content: DashboardContent | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

export function useExecutiveContent(): ExecutiveContentResult {
  const q = useQuery<WireExecutiveDashboard, Error>({
    queryKey: ['executive', 'dashboard'],
    queryFn: () => apiFetch<WireExecutiveDashboard>(EXECUTIVE_DASHBOARD_PATH),
    staleTime: 30_000,
  });

  return {
    content: q.data ? toDashboardContent(q.data) : null,
    isLoading: q.isLoading,
    isError: q.isError,
  };
}
